/* eslint-disable */
import type { Money } from "../../common/currency.pb";

/** InvestmentEntity types offer by Cash. */
export enum InvestmentEntityType {
  CURRENCY = "CURRENCY",
  STOCK = "STOCK",
  ETF = "ETF",
}

/** InvestmentEntity status, used to disable an offering. */
export enum InvestmentEntityStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

/**
 * OrderType is a detail passed to the market that will determine
 * the way the order will be executed. e.g.: MARKET, LIMIT, GTC.
 */
export enum OrderType {
  /** MARKET - A market order is filled as soon as possible on the market. */
  MARKET = "MARKET",
  /**
   * MARKET_WHEN_TOUCHED - A market order is created once the last trade price touches the target price.
   * Target price can be both above or below the current market price, for either BUY or SELL.
   */
  MARKET_WHEN_TOUCHED = "MARKET_WHEN_TOUCHED",
}

/** OrderSide is whether the order is to buy or sell an asset. */
export enum OrderSide {
  /** BUY - Order is for the purchase of an asset. */
  BUY = "BUY",
  /** SELL - Order is for the sale of an asset. */
  SELL = "SELL",
  /** SELL_ALL - Order is for the sale of all holdings of a particular asset. */
  SELL_ALL = "SELL_ALL",
}

export enum OrderStatus {
  /** EXECUTED - The order was successfully executed. */
  EXECUTED = "EXECUTED",
  /** CANCELED - The order was canceled. See OrderCancelationReason. */
  CANCELED = "CANCELED",
  /** PLACED - The order was sent to the market. */
  PLACED = "PLACED",
  /** REJECTED - Order was rejected. See OrderRejectionReason. */
  REJECTED = "REJECTED",
  /** SCHEDULED - The order is scheduled to be executed the next time the market open. */
  SCHEDULED = "SCHEDULED",
  /** PARTIALLY_EXECUTED - The order was partially filled, and will not fill any further. */
  PARTIALLY_EXECUTED = "PARTIALLY_EXECUTED",
}

/** Event type is used to create payment rows for activity that is not trades. */
export enum EventType {
  TRADE = "TRADE",
  DIVIDEND = "DIVIDEND",
  STOCK_BUYOUT = "STOCK_BUYOUT",
  STOCK_PAYOUT = "STOCK_PAYOUT",
  TAX_WITHHOLDING_REVERSAL = "TAX_WITHHOLDING_REVERSAL",
}

export enum StatementType {
  /** TRADE_CONFIRM - Daily record of prior day's trade activity. */
  TRADE_CONFIRM = "TRADE_CONFIRM",
  /** STOCK_TAX_FORM - Tax forms related to stock trading. */
  STOCK_TAX_FORM = "STOCK_TAX_FORM",
  /** STOCK_MONTHLY_STATEMENT - Monthly summary of activity related to stock trading. */
  STOCK_MONTHLY_STATEMENT = "STOCK_MONTHLY_STATEMENT",
  /** BTC_TAX_FORM - Bitcoin tax forms. */
  BTC_TAX_FORM = "BTC_TAX_FORM",
  /** BORROW - Borrow legal forms */
  BORROW = "BORROW",
  /** LENDING - Lending legal forms */
  LENDING = "LENDING",
}

/** Order rejection reason struct from invest-core repository. */
export interface OrderRejectionReason {
  /** Reason for rejection of the order. */
  reason?: OrderRejectionReasonReason;
  /** This field is populated only if reason = `SCHEDULED_ORDERS_DISABLED`. */
  next_market_open_time?: number;
  /** This field is populated only if reason = `CUSTOMER_CONTROLS_STOCK_BUYS_LIMIT_REACHED` */
  limits_reached?: OrderRejectionReasonLimitsReached;
}

export enum OrderRejectionReasonReason {
  /** INSUFFICIENT_FUNDS - Not enough money or shares to permit trade. */
  INSUFFICIENT_FUNDS = "INSUFFICIENT_FUNDS",
  /** EXCHANGE_DISABLED - Exchange not available to trade. */
  EXCHANGE_DISABLED = "EXCHANGE_DISABLED",
  /** INVESTMENT_ENTITY_NOT_TRADABLE - Investment entity currently disabled for trade. */
  INVESTMENT_ENTITY_NOT_TRADABLE = "INVESTMENT_ENTITY_NOT_TRADABLE",
  /** SERVER_FAILURE - Failed. */
  SERVER_FAILURE = "SERVER_FAILURE",
  /** PDT_RULES - The trade, if allowed, would have violated pattern day trading rules. */
  PDT_RULES = "PDT_RULES",
  /** PRE_EXISTING_SCHEDULED_ORDER - There is already a scheduled order for this customer and this entity. */
  PRE_EXISTING_SCHEDULED_ORDER = "PRE_EXISTING_SCHEDULED_ORDER",
  /** EXPIRED_QUOTE - The quote for this order is stale and needs to be regenerated. */
  EXPIRED_QUOTE = "EXPIRED_QUOTE",
  /** MARKET_CLOSED - Order was rejected because market closed before order could be executed. */
  MARKET_CLOSED = "MARKET_CLOSED",
  /** BLOCKED_DUE_TO_ABUSE_DETECTION - The customer has passed trade abuse thresholds. */
  BLOCKED_DUE_TO_ABUSE_DETECTION = "BLOCKED_DUE_TO_ABUSE_DETECTION",
  /** TRADE_AMOUNT_TOO_SMALL - The amount of the purchase was less than the required minimum (currently $1). */
  TRADE_AMOUNT_TOO_SMALL = "TRADE_AMOUNT_TOO_SMALL",
  /** CUSTOMER_TRADING_DISABLED - This customer is not allowed to trade. */
  CUSTOMER_TRADING_DISABLED = "CUSTOMER_TRADING_DISABLED",
  /**
   * PRE_EXISTING_ORDER_AWATING_TO_BE_FULFILLED - There is already an order awaiting to be fulfilled for this customer and this investment
   * entity.
   */
  PRE_EXISTING_ORDER_AWATING_TO_BE_FULFILLED = "PRE_EXISTING_ORDER_AWATING_TO_BE_FULFILLED",
  /** MARKET_HALT - Market is halted. */
  MARKET_HALT = "MARKET_HALT",
  /**
   * CUSTOM_ORDER_PRICE_TOO_CLOSE - The desired price for the custom (MARKET_WHEN_TOUCHED) order was too close (within $0.05) of
   * the market price (technically only for STOP orders).
   */
  CUSTOM_ORDER_PRICE_TOO_CLOSE = "CUSTOM_ORDER_PRICE_TOO_CLOSE",
  /**
   * CUSTOM_ORDER_LIMIT_EXCEEDED - The trade, if allowed, would exceed the limits for outstanding custom (MARKET_WHEN_TOUCHED)
   * orders (currently 3 BUYs and 1 SELL).
   */
  CUSTOM_ORDER_LIMIT_EXCEEDED = "CUSTOM_ORDER_LIMIT_EXCEEDED",
  /**
   * INSUFFICIENT_HOLDINGS - This SELL order plus outstanding custom (MARKET_WHEN_TOUCHED) SELL orders would exceed the
   * holdings of this entity.
   */
  INSUFFICIENT_HOLDINGS = "INSUFFICIENT_HOLDINGS",
  /**
   * CUSTOM_ORDER_TYPE_CHANGED - Due to market fluctuations, the type of custom order (STOP or MARKET_IF_TOUCHED) would be
   * different than the user intended.
   */
  CUSTOM_ORDER_TYPE_CHANGED = "CUSTOM_ORDER_TYPE_CHANGED",
  /**
   * INVESTING_ENTITY_DELISTED - Investment entity was delisted. Only sell all orders are supported for Over The
   * Counter (OTC) trades.
   */
  INVESTING_ENTITY_DELISTED = "INVESTING_ENTITY_DELISTED",
  /** PRE_EXISTING_FILLED_ORDER - Order has already executed and filled. */
  PRE_EXISTING_FILLED_ORDER = "PRE_EXISTING_FILLED_ORDER",
  /** SCHEDULED_ORDERS_DISABLED - Scheduled orders are disabled. */
  SCHEDULED_ORDERS_DISABLED = "SCHEDULED_ORDERS_DISABLED",
  /**
   * CLOSE_POSITIONS_ONLY - Buys for the given Investment Entity are blocked by clearing vendor due to capital
   * requirements. Only sells are allowed.
   */
  CLOSE_POSITIONS_ONLY = "CLOSE_POSITIONS_ONLY",
  /** CUSTOMER_DENYLISTED - Customers can't BUY while they're in a denylisted state. */
  CUSTOMER_DENYLISTED = "CUSTOMER_DENYLISTED",
  /** CUSTOMER_SUSPENDED - Customers can't BUY while they're in a suspended state. */
  CUSTOMER_SUSPENDED = "CUSTOMER_SUSPENDED",
  /** ORDER_QUANTITY_TOO_LARGE - DriveWealth order quantity cannot exceed 10000s */
  ORDER_QUANTITY_TOO_LARGE = "ORDER_QUANTITY_TOO_LARGE",
  /** CUSTOMER_CONTROLS_STOCK_BUYS_DISABLED - Customer stock buy control is disabled. */
  CUSTOMER_CONTROLS_STOCK_BUYS_DISABLED = "CUSTOMER_CONTROLS_STOCK_BUYS_DISABLED",
  /** CUSTOMER_CONTROLS_STOCK_BUYS_LIMIT_REACHED - Customer stock buy limit is reached. */
  CUSTOMER_CONTROLS_STOCK_BUYS_LIMIT_REACHED = "CUSTOMER_CONTROLS_STOCK_BUYS_LIMIT_REACHED",
  /** MISSING_AMOUNT_FOR_BUY_ORDER - Missing Amount for BUY order */
  MISSING_AMOUNT_FOR_BUY_ORDER = "MISSING_AMOUNT_FOR_BUY_ORDER",
  /** NO_ACTIVE_BROKERAGE_ACCOUNT - Customer does not have an active brokerage account. */
  NO_ACTIVE_BROKERAGE_ACCOUNT = "NO_ACTIVE_BROKERAGE_ACCOUNT",
  /** BROKERAGE_ACCOUNT_BLOCKED - The brokerage account has blocked the order (see the account status) */
  BROKERAGE_ACCOUNT_BLOCKED = "BROKERAGE_ACCOUNT_BLOCKED",
  /** POSITION_LOCKED - The positions are locked (see the position status) */
  POSITION_LOCKED = "POSITION_LOCKED",
  /** INVESTMENT_ENTITY_SELLABLE_ONLY - The requested investment entity can only be sold. */
  INVESTMENT_ENTITY_SELLABLE_ONLY = "INVESTMENT_ENTITY_SELLABLE_ONLY",
}

export interface OrderRejectionReasonLimitsReached {
  /** Limits that have been reached (not all limits this customer is subject to) */
  limits?: OrderRejectionReasonLimitsReachedLimit[];
}

export interface OrderRejectionReasonLimitsReachedLimit {
  /** The upper limit of a customer's invest buy limit, in their default fiat currency */
  customer_limit?: Money;
  /** The period of time (in days) that the limit applies for */
  period_days?: number;
}

export interface OrderCancelationReason {
  reason?: OrderCancelationReasonReason;
}

export enum OrderCancelationReasonReason {
  /** MANUAL - This order was canceled manually by the user. */
  MANUAL = "MANUAL",
  /** CUSTOM_ORDER_EXPIRATION - This order was canceled because it expired. */
  CUSTOM_ORDER_EXPIRATION = "CUSTOM_ORDER_EXPIRATION",
  /** CORPORATE_ACTION - This order was canceled due to a corporate action. */
  CORPORATE_ACTION = "CORPORATE_ACTION",
  /** DIVIDEND - This order was canceled due to a dividend payout. */
  DIVIDEND = "DIVIDEND",
}

/** InvestmentOrder is used to store order information on the DbTransferData table. */
export interface InvestmentOrder {
  order_token?: string;
  investment_entity_token?: string;
  instrument_token?: string;
  quote_token?: string;
  fee?: Money;
  order_type?: OrderType;
  order_side?: OrderSide;
  /**
   * Estimated execution time if the order was scheduled. This datetime is formatted
   * using ISO 8601 (e.g., 2019-03-18T14:30:00Z).
   */
  estimated_execution_time?: string;
  estimated_share_amount?: string;
  estimated_share_price?: Money;
  /**
   * The estimated amount of this order. This value will be the
   * `amount` in the request OR `units * quoted_share_price.`
   */
  estimated_amount?: Money;
  /** Investment Entity symbol. */
  symbol?: string;
  /** Investment Entity display name. */
  symbol_display_name?: string;
  /** This value is only set after the order is executed. */
  status?: OrderStatus;
  /** Final amount after the order has been executed. */
  final_amount?: Money;
  /** The rejection reason is only going to be present if status is `REJECTED`. */
  rejection_reason?: OrderRejectionReason;
  /** Timezone of the device when the order was created. */
  time_zone?: string;
  /** Pattern Day Trading Data will be present if this order is day trading. */
  pattern_day_trade_data?: PatternDayTradeData;
  /**
   * Expected settlement timestamp, with day granularity, formatted using ISO 8601
   * (e.g., 2019-03-18T14:30:00Z). Will only be set when sells cannot be
   * instantly settled.
   */
  settlement_time?: string;
  /** Custom Order Data is only set if this is a custom order. */
  custom_order?: CustomOrder;
  /**
   * The expiration timestamp for a custom (MARKET_WHEN_TOUCHED) order.
   * Only present for MARKET_WHEN_TOUCHED requests.
   */
  expiration_epoch_ms?: number;
  /** Only present if status is `CANCELED`. */
  cancelation_reason?: OrderCancelationReason;
  /**
   * DEPRECATED: Incentives is no longer an active product offering.
   * Incentive Data is only set if this is an incentive order.
   *
   * @deprecated
   */
  incentive_data?: IncentiveData;
  /**
   * If this is the first custom (MARKET_WHEN_TOUCHED) order.
   * Only present for MARKET_WHEN_TOUCHED requests.
   */
  is_first_market_when_touched_order?: boolean;
  /** The market type where the order was quoted. */
  quote_market_type?: InvestmentOrderMarketType;
}

export enum InvestmentOrderMarketType {
  /** OTC - Over-The-Counter market. */
  OTC = "OTC",
  /** STOCK_EXCHANGE - This umbrella includes auction and dealer markets such as NASDAQ and NYSE. */
  STOCK_EXCHANGE = "STOCK_EXCHANGE",
}

export interface PatternDayTradeData {
  /** The count of completed day trades within the past 5 market days. */
  count?: number;
  /**
   * The time when the day trade constraint is released. This datetime is formatted
   * using ISO 8601 (e.g., 2019-03-18T14:30:00Z).
   * Note this only applies to completed day trades, not potential day trades.
   */
  release_time?: string;
  /** The count of completed + potential (ie from pending orders) day trades. */
  count_with_potential?: number;
  /**
   * A list of investment entities for which there are pending orders that could
   * potentially cause day trades.
   */
  pending_investment_entity_display_names?: string[];
}

/** A customer's tax document, monthly statement or trade confirm statement. */
export interface Document {
  /** The name of the document. e.g.: "2019 Tax Form 1099-B" */
  name?: string;
  /** The identifier for the document. e.g.: "2019070801" */
  key?: string;
  /**
   * The timestamp when the document was created.
   *
   * @deprecated
   */
  issue_date?: number;
  /**
   * The redirect URL template to be used to retrieve the document as PDF.
   * The web login token is stripped in for authentication when it's time to
   * make the request.
   * e.g.: https://cash.app/login?token={{token}}&redirect=/invest/statements/redirect&file_key=2019111501
   * e.g.: https://cash.app/login?token={{token}}&redirect=/invest/statements/csv&asset=equity&start=20190101&end=20200101
   */
  url?: string;
  /** The type of the document. */
  type?: StatementType;
  /**
   * The date that this document is covering, for STOCK_MONTHLY_STATEMENT would represent the month, and
   * for STOCK_TAX_FORM would represent the year. Its unit is millisecond.
   */
  document_date?: number;
  /** True if server is able to send an email with a download link to the customer. */
  email_forwardable?: boolean;
}

/** Content and links to populate the My First Stock, My First Bitcoin client modules. */
export interface LearnMoreConfiguration {
  /** Text to be displayed for the click through link. */
  link_text?: string;
  /** Location the link will connect to. */
  link_url?: string;
  /** Location of the image displayed in the module. */
  image_url?: string;
  /** Text to be displayed as the title for the module. */
  title_text?: string;
  /** Text to be displayed in the main part of the module. */
  body_text?: string;
}

/** Equity tile content for discovery animation. */
export interface EquityDiscoveryAnimationTile {
  /** Investment entity token for equity represented in this tile. */
  token?: string;
  /** Image URL for this equity tile. */
  image_url?: string;
  /** Accent color for display while tile is loading. Format: `#ffffff` */
  accent_color?: string;
  /** Name of the equity on the tile. */
  name?: string;
}

/**
 * Configuration defining a period for which the associated order will stay on hold until the
 * target is hit.
 */
export interface CustomOrder {
  period_token?: string;
  /** The current price presented to the customer when this order was created. */
  original_share_price?: Money;
  /** Target share price for the order to be triggered. */
  target_share_price?: Money;
}

/**
 * DEPRECATED: Incentives is no longer an active product offering.
 *
 * @deprecated
 */
export interface IncentiveData {
  /** The incentive token applied to this order */
  incentive_token?: string;
  /**
   * The amount of money contributed by the incentive. The total cost to the user is
   * total(or estimated_amount in the case of a not executed order) - incentive_contribution.
   */
  incentive_contribution?: Money;
}

/**
 * Time agnostic structure for a period of time. To be used by the client to display all period
 * labels and send the selected period's token back to the server.
 */
export interface Period {
  token?: string;
  label?: string;
}

/** The configuration for custom orders which client will use to setup their UI and such. */
export interface CustomOrderConfiguration {
  /**
   * The available periods for which a custom order can be set. They should be displayed in the
   * order server sends them down.
   */
  periods?: Period[];
}

/** CryptoOrder is a copy of invest-crypto Order message. */
export interface CryptoOrder {
  token?: string;
  /** The side of the order (BUY or SELL). */
  side?: CryptoOrderOrderSide;
  /** The type of order (MARKET, CUSTOM, etc). */
  type?: CryptoOrderOrderType;
  /** The current state of order. */
  state?: CryptoOrderOrderState;
  /** The amount of crypto currency for this order. */
  crypto_amount?: Money;
  /** The amount of fiat currency for this order. */
  fiat_amount?: Money;
  /** The current price of the order (cf. [target_price]). */
  current_price?: Money;
  /**
   * The price at which this custom order will trigger, as chosen by the user.
   * Only present for CUSTOM orders.
   */
  target_price?: Money;
  /**
   * The current price as seen by client when this custom order was created.
   * Only present for CUSTOM orders.
   */
  original_price?: Money;
  /**
   * The calculated expiration timestamp for a custom order.
   * Only present for CUSTOM order requests.
   */
  expiration_epoch_ms?: number;
  /** The fee that will be assessed for processing this order. */
  fee?: Money;
  /**
   * Order Rejection Reason.
   * This is not part of the invest-crypto Order message.
   */
  order_rejection_reason?: CryptoOrderOrderRejectionReason;
}

export enum CryptoOrderOrderState {
  /** QUOTED - Order has been quoted, but not yet placed. */
  QUOTED = "QUOTED",
  /** ABANDONED - Quote grew stale and expired. It should not be executed. */
  ABANDONED = "ABANDONED",
  /** PLACED - Order was placed. */
  PLACED = "PLACED",
  /** FILLED - Order was completely filled. */
  FILLED = "FILLED",
  /** CANCELED - Order was canceled by the customer or by us. */
  CANCELED = "CANCELED",
  /** REJECTED - Order was rejected. */
  REJECTED = "REJECTED",
  /** EXPIRED - Order has expired (custom orders only). */
  EXPIRED = "EXPIRED",
}

export enum CryptoOrderOrderSide {
  BUY = "BUY",
  SELL = "SELL",
}

export enum CryptoOrderOrderType {
  MARKET = "MARKET",
  CUSTOM = "CUSTOM",
}

export interface CryptoOrderOrderRejectionReason {
  reason?: CryptoOrderOrderRejectionReasonReason;
}

export enum CryptoOrderOrderRejectionReasonReason {
  /** INSUFFICIENT_FUNDS - Customer has insufficient funds in their store balance to create or execute the order. */
  INSUFFICIENT_FUNDS = "INSUFFICIENT_FUNDS",
  /** INTERNAL_FAILURE - An internal error occurred and the trade could not be created / executed. */
  INTERNAL_FAILURE = "INTERNAL_FAILURE",
  /**
   * CUSTOM_ORDER_TYPE_CHANGED - The type of custom order does not match the customer's original intent due to market
   * fluctuations.
   */
  CUSTOM_ORDER_TYPE_CHANGED = "CUSTOM_ORDER_TYPE_CHANGED",
  /** ORDER_EXPIRED - The order already expired. */
  ORDER_EXPIRED = "ORDER_EXPIRED",
  /** LIMITS_EXCEEDED - Customer's order would exceed their account limits. */
  LIMITS_EXCEEDED = "LIMITS_EXCEEDED",
  /** RISK_FAILURE - Our risk systems have rejected this order. */
  RISK_FAILURE = "RISK_FAILURE",
  /**
   * CUSTOM_ORDER_LIMITS_EXCEEDED - Customer's order would exceed the customer's limits for open custom orders
   * (currently 3 BUYs 2 SELLs).
   */
  CUSTOM_ORDER_LIMITS_EXCEEDED = "CUSTOM_ORDER_LIMITS_EXCEEDED",
}

/** Extra payload data required when doing an invest payment. */
export interface InvestPaymentData {
  /** Investment entity token for the equity sent via the payment. */
  investment_entity_token?: string;
}
